import * as anchor from '@project-serum/anchor';
import { ASSOCIATED_TOKEN_PROGRAM_ID, getAssociatedTokenAddress, TOKEN_PROGRAM_ID } from '@solana/spl-token';
import { AnchorWallet } from '@solana/wallet-adapter-react';
import { Connection, PublicKey, SYSVAR_CLOCK_PUBKEY, SYSVAR_RENT_PUBKEY } from '@solana/web3.js';
import { sendAndConfirmTransactionListCustom1 } from 'solanacodes/utils';
import { Burn, IDL } from './idl';

export const CONTRACT_ID = 'H8EYyrrLrQ1DvhJpxAEYP8QGvUt7ghHNQ6wRRNRBLyLj';
export const WSOL_TOKEN_MINT = 'So11111111111111111111111111111111111111112';

export const loadProgram = (connection: anchor.web3.Connection, wallet: AnchorWallet): anchor.Program<Burn> => {
    const provider = new anchor.Provider(connection, wallet, {
        preflightCommitment: 'recent',
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const program: any = new anchor.Program(IDL as anchor.Idl, CONTRACT_ID, provider);
    return program;
};

export const SPL_ASSOCIATED_TOKEN_ACCOUNT_PROGRAM_ID = new anchor.web3.PublicKey(
    'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL'
);

export const getAtaForMint = async (
    mint: anchor.web3.PublicKey,
    buyer: anchor.web3.PublicKey
): Promise<[anchor.web3.PublicKey, number]> => {
    return await anchor.web3.PublicKey.findProgramAddress(
        [buyer.toBuffer(), TOKEN_PROGRAM_ID.toBuffer(), mint.toBuffer()],
        SPL_ASSOCIATED_TOKEN_ACCOUNT_PROGRAM_ID
    );
};

export const TOKEN_METADATA_PROGRAM_ID = new anchor.web3.PublicKey('metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s');

export const getMasterEditionAccount = async (mint: anchor.web3.PublicKey): Promise<anchor.web3.PublicKey> => {
    return (
        await anchor.web3.PublicKey.findProgramAddress(
            [Buffer.from('metadata'), TOKEN_METADATA_PROGRAM_ID.toBuffer(), mint.toBuffer(), Buffer.from('edition')],
            TOKEN_METADATA_PROGRAM_ID
        )
    )[0];
};

export const getMetadataAccount = async (mint: anchor.web3.PublicKey): Promise<anchor.web3.PublicKey> => {
    return (
        await anchor.web3.PublicKey.findProgramAddress(
            [Buffer.from('metadata'), TOKEN_METADATA_PROGRAM_ID.toBuffer(), mint.toBuffer()],
            TOKEN_METADATA_PROGRAM_ID
        )
    )[0];
};

export const getAllPDAs = async (
    prog: anchor.Program<Burn>,
    user: PublicKey = TOKEN_METADATA_PROGRAM_ID,
    nft: PublicKey = TOKEN_METADATA_PROGRAM_ID
) => {
    const [burnConfig, burnConfigBump] = await anchor.web3.PublicKey.findProgramAddress(
        [Buffer.from('config')],
        prog.programId
    );

    const [burnReceipt, burnReceiptBump] = await anchor.web3.PublicKey.findProgramAddress(
        [Buffer.from('receipt'), user.toBuffer(), nft.toBuffer()],
        prog.programId
    );

    const userNftTokenAccount = await getAssociatedTokenAddress(nft, user);

    const nftTokenMetadataAccount = await getMetadataAccount(nft);

    return {
        burnConfig,
        burnConfigBump,
        burnReceipt,
        burnReceiptBump,
        userNftTokenAccount,
        nftTokenMetadataAccount,
    };
};

export const burnNft = async ({
    anchorWallet,
    values,
    connection,
}: {
    anchorWallet: AnchorWallet;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    values: any;
    connection: Connection;
}) => {
    if (!anchorWallet) {
        throw new Error('Wallet is not connected, please connect');
    }
    const anchorProgram = await loadProgram(connection, anchorWallet);

    const txn = new anchor.web3.Transaction();

    const mint = new PublicKey(values.nftMint);

    const { burnConfig, burnReceipt, userNftTokenAccount, nftTokenMetadataAccount } = await getAllPDAs(
        anchorProgram,
        anchorWallet.publicKey,
        mint
    );

    txn.add(
        anchorProgram.instruction.burnNft({
            accounts: {
                user: anchorWallet.publicKey,
                nftTokenMint: mint,
                burnReceipt: burnReceipt,
                burnConfig: burnConfig,
                nftTokenMetadata: nftTokenMetadataAccount,
                userNftTokenAddress: userNftTokenAccount,
                tokenProgram: TOKEN_PROGRAM_ID,
                nftProgramId: TOKEN_METADATA_PROGRAM_ID,
                associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
                rent: SYSVAR_RENT_PUBKEY,
                time: SYSVAR_CLOCK_PUBKEY,
                systemProgram: anchor.web3.SystemProgram.programId,
            },
        })
    );

    await sendAndConfirmTransactionListCustom1(anchorWallet, connection, [txn])
};
